import { call, put, takeEvery, select, all } from 'redux-saga/effects';
import { gcApi } from 'config/axios';

import {
    REQUEST_CALENDAR_PREFERENCES,
    REQUEST_CALENDAR_WEEK,
    REQUEST_CALENDAR_MONTH,
    REQUEST_CALENDAR_NOTE,
    REQUEST_TRAINING_PLANS_FOR_DATE,
    REQUEST_CALENDAR_EXPORT,
    PUBLISH_CALENDAR_EXPORT,
    UNPUBLISH_CALENDAR_EXPORT,
    REQUEST_CALENDAR_YEAR,
    REQUEST_PRIMARY_EVENT_METADATA,
    REQUEST_CALENDAR_UPCOMING_EVENTS,
    FETCH_TRAINING_PLAN,
    FETCH_CALENDAR_EVENT,
    UPDATE_CALENDAR_PREFERENCES,
    SHOW_ADD_SIDEBAR,
    FETCH_TRAINING_PLAN_CALENDAR_NOTE,
    FETCH_UPCOMING_EVENTS,
    FETCH_UPCOMING_CAL_EVENTS,
    SEND_CALENDAR_ITEMS_TO_DEVICE,
    REQUEST_CALENDAR_UPCOMING_EVENTS_FOR_COURSE_ID,
    REQUEST_CALENDAR_UPCOMING_EVENTS_FOR_COURSE_ID_ERROR,
    REQUEST_CALENDAR_UPCOMING_EVENTS_FOR_COURSE_ID_SUCCESS
} from '../actions/ActionTypes';

import {
    receiveCalendarPreferences,
    receiveCalendarWeek,
    receiveCalendarMonth,
    receiveCalendarNote,
    receiveTrainingPlansForDate,
    receiveCalendarExport,
    requestCalendarExport,
    receiveCalendarYear,
    receivePrimaryEventMetadata,
    fetchUpcomingEventsPromise,
    requestTrainingPlan,
    receiveTrainingPlan,
    requestCalendarEvent,
    receiveCalendarEvent,
    calendarItemsSentToDevice,
    calendarItemsSentToDeviceError,
    receiveUpcomingEvents,
    requestUpcomingEvents,
    setCalendarSnackbar
} from '../actions/calendarActions';
import {
    setUserPreference,
    receiveUpcomingCalEvents,
    requestUpcomingCalEvents,
    receiveUpcomingEvents as receiveUpcomingEventsForProfilePage,
    hideAddSidebar
} from '../actions';

import { getCalendarSidebarContent, getCalendarPreferences } from '../reducers/pages/calendarPage';

import { handlingErrorCallback, handlingSuccessCallback } from './sagasUtils';
import i18next from 'i18next';

import { fetchAdaptiveCoachPlanSaga } from 'sagas/ftbAdaptiveCoachingTrainingPlanSagas';
import { getAdaptiveCoachPlan } from 'reducers/trainingPlan/fbtAdaptiveCoachingTrainingPlan';

import { EVENT_BASED_PLANS } from 'DSW/utils/DswUtils';

// worker
export function* fetchCalendarPreferences() {
    try {
        const response = yield gcApi.get('/calendar-service/preferences');

        yield put(receiveCalendarPreferences(response.data));
    } catch (e) {}
}

export function* updateCalendarPreferences({ calendarPreferences: prefs }) {
    yield put(receiveCalendarPreferences(prefs));

    if (prefs.atpPlans && prefs.atpPlans.length > 0) {
        yield put(setUserPreference('AdaptiveTrainingPlan.Calendar.Preference', JSON.stringify(prefs.atpPlans)));
    }
    // Need to remove the atpPlans from the calendar preferences since API doesn't support atpPlans
    let newPrefs = Object.assign({}, prefs);
    if ('atpPlans' in newPrefs) {
        delete newPrefs.atpPlans;
    }

    yield call(hideSidebarForPreferences, { calendarPreferences: prefs });

    try {
        const response = yield gcApi.put('/calendar-service/preferences', newPrefs);
    } catch (e) {}
}

/* Keep the calendar filters in sync with the sidebar state */

// if a filter is deselected and the sidebar for that type is open, close it
export function* hideSidebarForPreferences({ calendarPreferences: prefs }) {
    const sidebarContent = yield select(getCalendarSidebarContent);

    if (!prefs.showWorkout && sidebarContent.content === 'workout') {
        yield put(hideAddSidebar());
    } else if (!prefs.showGoal && sidebarContent.content === 'goal') {
        yield put(hideAddSidebar());
    }
}

// If the sidebar is open, enable its corresponding filter
export function* displayFiltersForSidebar({ content, date, editMode, data }) {
    const preferences = yield select(getCalendarPreferences);

    if (!preferences.showWorkout && content === 'workout') {
        yield put(setCalendarSnackbar(i18next.t('calendar:calendar_filter_update_workouts')));

        yield call(updateCalendarPreferences, {
            calendarPreferences: {
                ...preferences,
                showWorkout: true
            }
        });
    } else if (!preferences.showGoal && content === 'goal') {
        yield put(setCalendarSnackbar(i18next.t('calendar:calendar_filter_update_goals')));

        yield call(updateCalendarPreferences, {
            calendarPreferences: {
                ...preferences,
                showGoal: true
            }
        });
    }
}

const filterCompletedTrainingPlans = function*(calendarItems) {
    const filteredTp = calendarItems.map((item) => {
        return call(function*() {
            if (item.itemType === 'trainingPlan' && !item.isStart) {
                yield call(fetchAdaptiveCoachPlanSaga, { trainingPlanId: item.trainingPlanId });
                const tp = yield select((state) => getAdaptiveCoachPlan(state));
                // Don't show event training planas that have ended. They will have events display with the same name
                // which will be confusing to the user
                if (tp && !EVENT_BASED_PLANS.includes(tp.trainingSubType.subTypeKey)) {
                    return item;
                }
            } else {
                return item;
            }
        });
    });

    const filtered = yield all(filteredTp);
    return filtered.filter((x) => x !== undefined);
};

export function* fetchCalendarWeek({ year, month, day, firstDayOfWeek, groupId, append = false }) {
    try {
        let urlString = `/calendar-service/year/${year}/month/${month}/day/${day}/start/${firstDayOfWeek}`;

        if (groupId) {
            urlString = `/calendar-service/group/${groupId}/year/${year}/month/${month}/day/${day}/start/${firstDayOfWeek}`;
        }

        const response = yield gcApi.get(urlString);

        const data = yield filterCompletedTrainingPlans(response.data.calendarItems);
        response.data.calendarItems = data;
        yield put(receiveCalendarWeek(response.data, append));
    } catch (e) {
        yield put(receiveCalendarWeek(null, append));
    }
}

export function* fetchCalendarMonth({ year, month, groupId, append = false }) {
    try {
        let urlString = `/calendar-service/year/${year}/month/${month}`;

        if (groupId) {
            urlString = `/calendar-service/group/${groupId}/year/${year}/month/${month}`;
        }

        const response = yield gcApi.get(urlString);
        const data = yield filterCompletedTrainingPlans(response.data.calendarItems);
        response.data.calendarItems = data;
        yield put(receiveCalendarMonth(response.data, append));
    } catch (e) {
        yield put(receiveCalendarMonth(null, append));
    }
}

export function* fetchCalendarNote({ noteId }) {
    try {
        const response = yield gcApi.get(`/calendar-service/note/${noteId}`);

        yield put(receiveCalendarNote(response.data, noteId));
    } catch (e) {
        yield put(receiveCalendarNote(null, noteId));
    }
}

export function* fetchTrainingPlanCalendarNote({ noteId }) {
    try {
        const response = yield gcApi.get(`/trainingplan-service/trainingplan/scheduled/notes/${noteId}`);

        yield put(receiveCalendarNote(response.data, noteId));
    } catch (e) {
        yield put(receiveCalendarNote(null, noteId));
    }
}

export function* fetchTrainingPlansForDate({ date }) {
    try {
        const response = yield gcApi.get(`/trainingplan-service/trainingplan/active-plans?startDate=${date}`);

        yield put(receiveTrainingPlansForDate(response.data, date));
    } catch (e) {
        yield put(receiveTrainingPlansForDate(null, date));
    }
}

export function* fetchCalendarExport(callback) {
    try {
        const response = yield gcApi.get('/calendar-service/export');

        yield put(receiveCalendarExport(response.data));
    } catch (e) {
        yield put(receiveCalendarExport(null));
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* publishCalendarExport({ data, callback }) {
    try {
        const response = yield gcApi.post('/calendar-service/export', data);

        yield put(receiveCalendarExport(response.data));
    } catch (e) {
        yield put(receiveCalendarExport(null));
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* unPublishCalendarExport({ id, callback }) {
    try {
        const response = yield gcApi.delete(`/calendar-service/export/${id}`);

        yield put(requestCalendarExport());
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchCalendarYear({ year, groupId }) {
    try {
        let url = `/calendar-service/year/${year}`;

        if (groupId) {
            url = `/calendar-service/group/${groupId}/year/${year}`;
        }

        const response = yield gcApi.get(url);
        yield put(receiveCalendarYear(response.data));
    } catch (e) {}
}

export function* fetchPrimaryEventMetadata() {
    try {
        const response = yield gcApi.get('/calendar-service/event/primary', {
            validateStatus: (status) => {
                return status === 404 || status === 200 || status === 204;
            }
        });

        let data = {};
        // Have to check since we may get a 404
        if (response && response.data && !response.data.error) {
            data = response.data;
        }
        yield put(receivePrimaryEventMetadata(data));
    } catch (e) {
        yield put(receivePrimaryEventMetadata({}));
    }
}

export function* fetchUpcomingEvents({ numDaysForward, limit, callback }) {
    try {
        const response = yield fetchUpcomingEventsPromise({ numDaysForward, limit });
        yield put(receiveUpcomingEvents(response.data));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {}
}

export function* fetchUpcomingEventsForCourseId({ courseId }) {
    try {
        const response = yield call(gcApi.get, `/calendar-service/events/?courseId=${courseId}`);
        yield put({
            type: REQUEST_CALENDAR_UPCOMING_EVENTS_FOR_COURSE_ID_SUCCESS,
            response: response
        });
    } catch (e) {
        yield put({
            type: REQUEST_CALENDAR_UPCOMING_EVENTS_FOR_COURSE_ID_ERROR
        });
    }
}

export function* fetchTrainingPlan({ tpId, callback }) {
    try {
        yield put(requestTrainingPlan());

        const response = yield gcApi.get(`/trainingplan-service/trainingplan/${tpId}`);
        yield put(receiveTrainingPlan(response.data));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield put(receiveTrainingPlan(null));
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchCalendarEvent({ eventId, groupId, shareable, callback }) {
    try {
        yield put(requestCalendarEvent());

        let url = '/calendar-service/';
        if (groupId) {
            url += `group/${groupId}/`;
        }
        url += `event/${eventId}`;
        if (shareable) {
            url += `/shareable`;
        }

        const response = yield gcApi.get(url);
        yield put(receiveCalendarEvent(response.data, eventId));
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield put(receiveCalendarEvent({}, eventId));
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* sendCalendarItemsToDevice() {
    try {
        const response = yield gcApi.put('/workout-service/schedule/sendtodevices');
        yield put(calendarItemsSentToDevice());
    } catch (e) {
        yield put(calendarItemsSentToDeviceError());
    }
}

export function* fetchUpcomingEventsSaga({ displayName, guid, startDate, limit, callback }) {
    try {
        yield put(requestUpcomingEvents(displayName));
        let url = `calendar-service/events/upcoming?calendarOwnerGuid=${guid}&startDate=${startDate}&limit=${limit}&numDaysForward=365`;
        const response = yield gcApi.get(url);
        yield put(receiveUpcomingEventsForProfilePage(displayName, response.data)); // This needs to use receiveUpcomingEvents() from /actions/index.js NOT receiveUpcomingEvents() from /calendarActions.js, hence the import alias
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* fetchUpcomingCalEventsSaga({ guid, startDate, limit, numDaysForward }) {
    try {
        yield put(requestUpcomingCalEvents());
        let url = `calendar-service/events/upcoming?startDate=${startDate}&limit=${limit}&numDaysForward=${numDaysForward}`;

        if (guid) {
            url += `&calendarOwnerGuid=${guid}`;
        }
        const response = yield gcApi.get(url);
        yield put(receiveUpcomingCalEvents(response.data));
    } catch (e) {}
}

// watcher
export const calendarSagas = [
    takeEvery(REQUEST_CALENDAR_PREFERENCES, fetchCalendarPreferences),
    takeEvery(UPDATE_CALENDAR_PREFERENCES, updateCalendarPreferences),
    takeEvery(SHOW_ADD_SIDEBAR, displayFiltersForSidebar),
    takeEvery(REQUEST_CALENDAR_WEEK, fetchCalendarWeek),
    takeEvery(REQUEST_CALENDAR_MONTH, fetchCalendarMonth),
    takeEvery(REQUEST_CALENDAR_NOTE, fetchCalendarNote),
    takeEvery(FETCH_TRAINING_PLAN_CALENDAR_NOTE, fetchTrainingPlanCalendarNote),
    takeEvery(REQUEST_TRAINING_PLANS_FOR_DATE, fetchTrainingPlansForDate),
    takeEvery(REQUEST_CALENDAR_EXPORT, fetchCalendarExport),
    takeEvery(PUBLISH_CALENDAR_EXPORT, publishCalendarExport),
    takeEvery(UNPUBLISH_CALENDAR_EXPORT, unPublishCalendarExport),
    takeEvery(REQUEST_CALENDAR_YEAR, fetchCalendarYear),
    takeEvery(REQUEST_PRIMARY_EVENT_METADATA, fetchPrimaryEventMetadata),
    takeEvery(REQUEST_CALENDAR_UPCOMING_EVENTS, fetchUpcomingEvents),
    takeEvery(REQUEST_CALENDAR_UPCOMING_EVENTS_FOR_COURSE_ID, fetchUpcomingEventsForCourseId),
    takeEvery(FETCH_TRAINING_PLAN, fetchTrainingPlan),
    takeEvery(FETCH_CALENDAR_EVENT, fetchCalendarEvent),
    takeEvery(SEND_CALENDAR_ITEMS_TO_DEVICE, sendCalendarItemsToDevice),
    takeEvery(FETCH_UPCOMING_EVENTS, fetchUpcomingEventsSaga),
    takeEvery(FETCH_UPCOMING_CAL_EVENTS, fetchUpcomingCalEventsSaga)
];
